import React, { useState, useEffect, useMemo, useRef } from "react";
import { Box, Stack, Collapse } from "@mui/material";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import {
  CustomModal,
  CustomTextInput,
  CustomSingleSelect,
  CustomFileUploader,
  CustomButton,
  CustomCategorySelect,
  CustomSectionTitle,
  FixedActionBar,
  type Control,
  type UseFormRegister,
  type FieldErrors,
  type CategoryOption,
} from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import { fetchCategoryManagement } from "@/store/categoryManagementSlice";
// Equipment 廃止 → Unit (area_type=facility) で代替
import { fetchUnitsByProperty } from "@/store/unitSlice";
import type { Unit as UnitRecord, AreaType } from "@ippon/shared-types";
import { AREA_TYPE_OPTIONS } from "@ippon/shared-types";
import ipponApi from "@/config/axios";
import { Can } from "@/components/auth/Can";
import { OPTION_CODES } from "@/constants/optionCodes";

/**
 * 修繕対象 target_type (PropertySection.section_category と一致する 5 分類)
 */
export type RepairTargetType =
  | "private_area"
  | "common_area"
  | "land"
  | "other"
  | "facility";

/**
 * scope (= area_type) → target_type (section_category 形式) マッピング。
 * private → private_area / common → common_area / facility/land/other はそのまま。
 */
export const scopeToTargetType = (
  scope?: AreaType,
): RepairTargetType | undefined => {
  if (scope === "private") return "private_area";
  if (scope === "common") return "common_area";
  if (scope === "land") return "land";
  if (scope === "other") return "other";
  if (scope === "facility") return "facility";
  return undefined;
};

export type RepairTargetInfo = {
  target_type?: RepairTargetType;
  target_id?: string;
  target_label?: string;
};

const BaseURL = import.meta.env.VITE_API_URL;

type RepairFormValues = {
  title: string;
  scope: AreaType | ""; // 場所区分 (private/common/facility/land/other)
  area: string; // エリア名
  categories: string[]; // カテゴリ
  expenditure_type: string;
  repair_images?: any[];
  estimate_images?: any[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  control: Control<RepairFormValues>;
  register: UseFormRegister<RepairFormValues>;
  errors: FieldErrors<RepairFormValues>;
  setValue: (name: keyof RepairFormValues, value: any) => void;
  onSubmit: () => void;
  isSubmitting?: boolean;
  /** 親フォームの dirty 状態。モーダルを未保存で閉じようとした時の確認に使う。 */
  isDirty?: boolean;
  propertyId: string | null;
  watch: (name: keyof RepairFormValues) => any;
  /**
   * 設備/付帯設備/専有部 を対象にした修繕を登録する時に、親に target_type/target_id を伝える。
   * 互換: 未指定の親では単に無視される。
   */
  onTargetChange?: (info: RepairTargetInfo) => void;
  /** ページ表示モード: ドロワー外装を外して、ページ直下に form を描画 */
  asPage?: boolean;
  /** PreviewDrawer 埋め込み時 true (FixedActionBar を sticky に切替) */
  embedded?: boolean;
};

/**
 * 修繕対象の選択肢 (物件構成タブと一致する 5 分類)。
 * 共通定数 (shared-types/AREA_TYPE_OPTIONS) を再利用。
 */
const scopeOptions = AREA_TYPE_OPTIONS;

const initialCategoryOptions: CategoryOption[] = [
  { label: "衛生器具", value: "衛生器具" },
  { label: "外装材", value: "外装材" },
  { label: "躯体等", value: "躯体等" },
  { label: "建具類", value: "建具類" },
  { label: "外部金物等", value: "外部金物等" },
  { label: "自動ドア・シャッター等", value: "自動ドア・シャッター等" },
  { label: "内装", value: "内装" },
  { label: "外構（舗装等）", value: "外構（舗装等）" },
  { label: "変電設備", value: "変電設備" },
  { label: "動力盤、電灯盤、ケーブル等", value: "動力盤、電灯盤、ケーブル等" },
  { label: "電灯コンセント及び配線", value: "電灯コンセント及び配線" },
  { label: "照明設備", value: "照明設備" },
  { label: "電話・TV・放送設備等", value: "電話・TV・放送設備等" },
  { label: "発電機・蓄電池等", value: "発電機・蓄電池等" },
  { label: "空調換気設備", value: "空調換気設備" },
  { label: "受水槽・高架水槽・ポンプ", value: "受水槽・高架水槽・ポンプ" },
  { label: "給水設備", value: "給水設備" },
  { label: "給湯設備", value: "給湯設備" },
  { label: "排水設備", value: "排水設備" },
  { label: "配管設備", value: "配管設備" },
  { label: "昇降機設備（ELV）", value: "昇降機設備（ELV）" },
  { label: "機械式駐車場", value: "機械式駐車場" },
  { label: "消火設備", value: "消火設備" },
  { label: "警報設備", value: "警報設備" },
  { label: "避雷設備", value: "避雷設備" },
  { label: "その他", value: "その他" },
  { label: "屋上防水・屋根", value: "屋上防水・屋根" },
  { label: "防火設備", value: "防火設備" },
  { label: "消防設備点検（複合）", value: "消防設備点検（複合）" },
  { label: "特定建築物定期検査（複合）", value: "特定建築物定期検査（複合）" },
  { label: "防火設備点検（複合）", value: "防火設備点検（複合）" },
  { label: "建築設備定期検査（複合）", value: "建築設備定期検査（複合）" },
  { label: "廃棄物処理", value: "廃棄物処理" },
  {
    label: "非常照明（建築設備、特建、消防）",
    value: "非常照明（建築設備、特建、消防）",
  },
  { label: "誘導灯", value: "誘導灯" },
  { label: "法定点検実施費用", value: "法定点検実施費用" },
  { label: "排煙設備", value: "排煙設備" },
  { label: "リニューアル工事", value: "リニューアル工事" },
  { label: "原状回復工事", value: "原状回復工事" },
];

const RepairWorkCreateModal: React.FC<Props> = ({
  open,
  onClose,
  control,
  register,
  errors,
  setValue,
  onSubmit,
  isSubmitting,
  isDirty = false,
  propertyId,
  watch,
  onTargetChange,
  asPage = false,
  embedded = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>(
    initialCategoryOptions,
  );
  const [areaOptions, setAreaOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);

  // Equipment マスタは廃止。Unit (area_type=facility) を全体設備として扱う。
  const equipmentList = useSelector((s: RootState) =>
    s.units.list.data.filter((u) => u.area_type === "facility"),
  );

  const selectedScope = watch("scope");
  const titleValue = watch("title");
  const areaValue = watch("area");
  const { record: categoryRecord } = useSelector(
    (state: RootState) => state.categoryManagement,
  );

  // いずれか1つでも値が入っているかチェック
  const isFormValid = useMemo(() => {
    const hasTitle = titleValue && String(titleValue).trim().length > 0;
    const hasScope = selectedScope && String(selectedScope).length > 0;
    const hasArea = areaValue && String(areaValue).trim().length > 0;
    const hasCategories = selectedCategories && selectedCategories.length > 0;
    // いずれか1つでも値が入っていれば有効化
    return hasTitle || hasScope || hasArea || hasCategories;
  }, [titleValue, selectedScope, areaValue, selectedCategories]);

  const handleCategoriesChange = (categories: string[]) => {
    setSelectedCategories(categories);
    setValue("categories", categories);
  };

  const handleAddNewCategory = (newCategory: string) => {
    const newOption: CategoryOption = {
      label: newCategory,
      value: newCategory,
    };
    const nextOptions = [...categoryOptions, newOption];
    const nextSelected = [...selectedCategories, newCategory];
    setCategoryOptions(nextOptions);
    setSelectedCategories(nextSelected);
    setValue("categories", nextSelected);
  };

  const handleDeleteCategoryOption = (categoryValue: string) => {
    const nextOptions = categoryOptions.filter(
      (o) => o.value !== categoryValue,
    );
    setCategoryOptions(nextOptions);
    if (selectedCategories.includes(categoryValue)) {
      const nextSelected = selectedCategories.filter(
        (c) => c !== categoryValue,
      );
      setSelectedCategories(nextSelected);
      setValue("categories", nextSelected);
    }
  };

  // 場所区分の前回値を覚えて、初回マウント (= 親からのプリセット) では area をクリアしないようにする
  const prevScopeRef = useRef<string | null>(null);

  // initialAreaId による area プリセット時、areaOptions が揃ったら
  // target_label を逆引きして親 (onTargetChange) に通知する。
  // これをやらないと送信時に target_label が空のまま、area_name に unit_id (UUID) が入る。
  const labelSyncedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!areaValue || areaOptions.length === 0) return;
    if (labelSyncedRef.current === areaValue) return;
    const opt = areaOptions.find((o) => o.value === areaValue);
    if (!opt) return;
    labelSyncedRef.current = areaValue;
    onTargetChange?.({
      target_type: scopeToTargetType(selectedScope as any),
      target_id: areaValue,
      target_label: opt.label,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaValue, areaOptions]);

  // 共用部/専有部/付帯設備/設備 の選択に応じて対象選択肢を取得
  useEffect(() => {
    const prevScope = prevScopeRef.current;
    const scopeChangedByUser =
      prevScope !== null && prevScope !== selectedScope;
    prevScopeRef.current = selectedScope || null;

    if (!propertyId || !selectedScope) {
      setAreaOptions([]);
      if (scopeChangedByUser) setValue("area", "");
      onTargetChange?.({
        target_type: undefined,
        target_id: undefined,
        target_label: undefined,
      });
      return;
    }

    // 選択肢切り替え時のみリセット (初回プリセットでは area を保持)
    if (scopeChangedByUser) setValue("area", "");

    // 建物設備 = Unit (area_type=facility) を Redux 経由で取得
    if (selectedScope === "facility") {
      setAreaOptions([]);
      dispatch(fetchUnitsByProperty({ property_id: propertyId }));
      return;
    }

    // 専有部 / 共用部 / 敷地 / その他: Unit の area_type で絞り込み
    const areaType = selectedScope;

    const fetchAreas = async () => {
      setIsLoadingAreas(true);
      try {
        const res = await ipponApi.get(`${BaseURL}/api/v1/units`, {
          params: { property_id: propertyId, area_type: areaType },
        });
        const items = res.data?.data?.items;
        const units = Array.isArray(items) ? items : [];
        const options = units.map((unit: any) => ({
          label: String(unit.landmark_name || ""),
          // value は unit_id (target_id として BE に送る用)
          value: String(unit.unit_id || ""),
        }));
        setAreaOptions(options);
      } catch (error) {
        console.error("エリア取得エラー:", error);
        setAreaOptions([]);
      } finally {
        setIsLoadingAreas(false);
      }
    };

    fetchAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId, selectedScope, dispatch]);

  // カテゴリ管理の初期ロード（一度だけ実行）
  useEffect(() => {
    if (!open) return;
    dispatch(fetchCategoryManagement())
      .unwrap()
      .then((rec: any) => {
        if (rec && Array.isArray(rec.categories)) {
          const mergedValues = Array.from(
            new Set([
              ...initialCategoryOptions.map((o) => o.value),
              ...rec.categories,
            ]),
          );
          setCategoryOptions(
            mergedValues.map((v) => ({ label: String(v), value: String(v) })),
          );
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ストアのカテゴリ管理レコードが更新されたら同期（変更があったときだけ）
  useEffect(() => {
    if (!categoryRecord || !Array.isArray(categoryRecord.categories)) {
      return;
    }

    setCategoryOptions((prevOptions) => {
      const currentValues = new Set(prevOptions.map((o) => o.value));
      const newCategories = categoryRecord.categories || [];
      const hasChanged =
        newCategories.length !== currentValues.size ||
        newCategories.some((v: string) => !currentValues.has(v));

      if (!hasChanged) {
        return prevOptions;
      }

      const mergedValues = Array.from(
        new Set([
          ...initialCategoryOptions.map((o) => o.value),
          ...newCategories,
        ]),
      );
      return mergedValues.map((v) => ({ label: String(v), value: String(v) }));
    });
  }, [categoryRecord]);

  // asPage 時は CustomDrawer を外し、修繕詳細と同じレイアウト (CustomSectionTitle) で描画
  // NOTE: FormShell を関数内で定義すると毎レンダーで新しいコンポーネント型となり、
  // 子要素 (フォーム) が remount されて入力値が失われる。JSX を直接展開すること。
  // embedded（registerModal 内描画）時は asPage を維持しつつ、外装を上書きして
  // .registerModalBody / .registerModalFooter のレイアウトに統一する。
  // asPage でないとき（物件一覧の CustomModal 等）も同じ CSS を当てないと
  // .modalBox.registerModal の overflow:hidden 下端で登録ボタンが見切れる。
  const isModalEmbed = asPage && embedded;
  const useRegisterModalLayout = isModalEmbed || !asPage;
  const formBody = (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: useRegisterModalLayout ? 1 : undefined,
        minHeight: useRegisterModalLayout ? 0 : "100%",
      }}
    >
      {/* スクロール可能なコンテンツエリア */}
      <Box
        className={useRegisterModalLayout ? "registerModalBody" : undefined}
        sx={
          useRegisterModalLayout
            ? undefined
            : {
                flex: 1,
                overflowY: asPage ? "visible" : "auto",
                p: 0,
              }
        }
      >
        <Box sx={useRegisterModalLayout ? { maxWidth: "600px" } : undefined}>
          {asPage && <CustomSectionTitle title="基本情報" size="small" />}
          <Box sx={asPage ? { mt: 2 } : undefined}>
            <CustomTextInput
              label="工事タイトル"
              name="title"
              placeholder="工事のタイトルを入力"
              register={register}
              errors={errors}
              isRequired
              labelWidth="120px"
              inputWidth="100%"
            />

            <CustomSingleSelect
              label="場所区分"
              name="scope"
              placeholder="選択してください"
              control={control}
              errors={errors}
              isRequired
              options={[...scopeOptions]}
              labelWidth="120px"
              inputWidth="100%"
            />

            <Collapse in={!!selectedScope} timeout={400}>
              <Box
                sx={{
                  ml: 3,
                  position: "relative",
                  transition: "all 0.4s ease-out",
                  transform: selectedScope
                    ? "translateX(0)"
                    : "translateX(-20px)",
                  opacity: selectedScope ? 1 : 0,
                }}
              >
                {/* 場所区分のサブ要素であることを示す └ アイコン */}
                <SubdirectoryArrowRightIcon
                  sx={{
                    position: "absolute",
                    left: -20,
                    top: 0,
                    fontSize: 16,
                    color: "#989898",
                    pointerEvents: "none",
                  }}
                />
                {/* 専有部 / 共用部 / 敷地 / その他: PropertySection を選択 */}
                {(selectedScope === "private" ||
                  selectedScope === "common" ||
                  selectedScope === "land" ||
                  selectedScope === "other") && (
                  <CustomSingleSelect
                    label="名称"
                    name="area"
                    placeholder={
                      isLoadingAreas
                        ? "読み込み中..."
                        : areaOptions.length === 0
                          ? "該当する区画がありません"
                          : "選択してください"
                    }
                    control={control}
                    errors={errors}
                    options={areaOptions}
                    labelWidth="120px"
                    inputWidth="100%"
                    disabled={isLoadingAreas || areaOptions.length === 0}
                    onChangeValue={(value: any) => {
                      const id = String(value || "");
                      const opt = areaOptions.find((o) => o.value === id);
                      onTargetChange?.({
                        target_type: scopeToTargetType(selectedScope),
                        target_id: id,
                        target_label: opt?.label || "",
                      });
                    }}
                  />
                )}

                {/* 建物設備: 既存の Unit (facility) マスタから選択 */}
                {selectedScope === "facility" && (
                  <>
                    <CustomSingleSelect
                      label="名称"
                      name="area"
                      placeholder={
                        equipmentList.length === 0
                          ? "該当する区画がありません"
                          : "選択してください"
                      }
                      control={control}
                      errors={errors}
                      options={equipmentList.map((u: UnitRecord) => ({
                        label: u.landmark_name,
                        value: u.unit_id,
                      }))}
                      labelWidth="120px"
                      inputWidth="100%"
                      disabled={equipmentList.length === 0}
                      onChangeValue={(value: any) => {
                        const v = String(value || "");
                        const u = equipmentList.find(
                          (x: UnitRecord) => x.unit_id === v,
                        );
                        onTargetChange?.({
                          target_type: "facility",
                          target_id: v,
                          target_label: u?.landmark_name || "",
                        });
                      }}
                    />
                  </>
                )}
              </Box>
            </Collapse>
            <CustomSingleSelect
              label="支出区分"
              name="expenditure_type"
              placeholder="選択してください"
              control={control}
              errors={errors}
              options={[
                { label: "CAPEX（資本的支出）", value: "capex" },
                { label: "OPEX（工事費）", value: "opex" },
              ]}
              labelWidth="120px"
              inputWidth="100%"
            />
            <CustomCategorySelect
              label="カテゴリ"
              name="categories"
              options={categoryOptions}
              selectedCategories={selectedCategories}
              onCategoriesChange={handleCategoriesChange}
              onAddNewCategory={handleAddNewCategory}
              onDeleteCategoryOption={handleDeleteCategoryOption}
              errors={errors}
              labelWidth="120px"
              inputWidth="100%"
            />
          </Box>

          <CustomFileUploader
            label="画像"
            name="repair_images"
            setValue={(n: any, v: any) =>
              setValue(n as keyof RepairFormValues, v)
            }
            register={register as any}
            width="180px"
            initialImages={[]}
          />

          <Can feature={OPTION_CODES.ESTIMATE_AI}>
          {asPage ? (
            <Box mt={4}>
              <CustomSectionTitle
                title="工事見積書（※AI査定対象）"
                size="small"
              />
              <Box sx={{ mt: 2 }}>
                <CustomFileUploader
                  label=""
                  name="estimate_images"
                  setValue={(n: any, v: any) =>
                    setValue(n as keyof RepairFormValues, v)
                  }
                  register={register as any}
                  type="multiple"
                  width="354px"
                  initialImages={[]}
                />
              </Box>
            </Box>
          ) : (
            <CustomFileUploader
              label="工事見積書（※AI査定対象）"
              name="estimate_images"
              setValue={(n: any, v: any) =>
                setValue(n as keyof RepairFormValues, v)
              }
              register={register as any}
              type="multiple"
              width="354px"
              initialImages={[]}
            />
          )}
          </Can>
        </Box>
      </Box>

      {/* 固定ボタンエリア: registerModal 内 → registerModalFooter / 単独ページ → FixedActionBar / ドロワー → 中央ボタン */}
      {isModalEmbed ? (
        <Box className="registerModalFooter">
          <CustomButton
            label="戻る"
            buttonCategory="cancelModal"
            onClick={() => onClose?.()}
            disabled={isSubmitting}
            sx={{ minWidth: "140px !important", width: "140px" }}
          />
          <CustomButton
            label={isSubmitting ? "登録中..." : "登録"}
            type="submit"
            buttonCategory="submit"
            disabled={!isFormValid || isSubmitting}
            isLoading={isSubmitting}
            sx={{ minWidth: "140px !important", width: "140px" }}
          />
        </Box>
      ) : asPage ? (
        <FixedActionBar
          submitLabel={isSubmitting ? "登録中..." : "登録"}
          isLoading={isSubmitting}
          disabled={!isFormValid || isSubmitting}
          embedded={embedded}
        />
      ) : (
        <Box className="registerModalFooter">
          <Stack direction="row" spacing={1} justifyContent="center">
            <CustomButton
              label={isSubmitting ? "登録中..." : "登録"}
              type="submit"
              disabled={!isFormValid || isSubmitting}
              isLoading={isSubmitting}
              sx={{ minWidth: "140px !important", width: "140px" }}
            />
          </Stack>
        </Box>
      )}
    </Box>
  );

  return asPage ? (
    isModalEmbed ? (
      <>{formBody}</>
    ) : (
      <Box pb={9} pt={{ xs: 2, sm: 3 }}>
        <Box sx={{ mb: 4 }}>
          <CustomSectionTitle title="工事登録" size="large" addBorder={false} />
        </Box>
        {formBody}
      </Box>
    )
  ) : (
    <CustomModal
      openModal={open}
      handleCloseModal={onClose}
      title="工事登録"
      modalType="registerModal"
      addTitleBorder
      confirmOnDirtyClose={isDirty}
    >
      {formBody}
    </CustomModal>
  );
};

export default RepairWorkCreateModal;
